import React, { useState } from "react"
import ModalForm from "../modalForm/ModalForm.compunds"
import { InputCheckbox } from "../../atoms/InputCheckbox.atom"
import MyDatePicker from "../../atoms/DatePicker.atom"
import Dropdown from "../../atoms/Dropdown.atom"
import { compareDates } from "../../../utils/date.utils"
import { clsx } from "../../../utils/strings.utils"
import ClientChip from "../../molecules/ClientChip.molecule"
import ClientProjectChip from "../../molecules/ClientProjectChip.molecule"
import ProjectChip from "../../molecules/ProjectChip.molecule"
import InputText from "../../atoms/InputText.atom"
import DropdownCategoryFilter from "../DropdownCategoryFilter.compound"
import DropdownInput from "../../molecules/DropdownInput.molecule"
import MessageRect from "../../atoms/MessageRect.atom"
import { bulkEditTasks } from "../../../services/task.service"
import {
  resetCategory,
  resetComment,
  resetDate,
  resetFields,
  resetModule,
  resetProject,
  resetDeleteClientBilledTime,
  updateCategory,
  updateComment,
  updateDate,
  updateModule,
  updateDeleteClientBilledTime,
  resetTimeDifferenceComment,
  updateTimeDifferenceComment,
} from "./state/actions"
import { useFormState as useModalBulkEditTaskFormState } from "./state/useFormState"
import InputSwitchForm from "../../molecules/InputSwitchForm.molecule"
import ErrorMessageForm from "../../atoms/ErrorMessageForm.atom"
import { DateTime } from "luxon"

/** @typedef {import("../../../services/reports.service.types").IndividualTaskReportItem} IndividualTaskReportItem */

const checkboxIcon = (onClick) => (
  <i style={{ color: "white" }} className="pi pi-pencil" onClick={onClick} />
)

/**
 * @param {Object} props
 * @param {boolean} props.visible
 * @param {Function} props.setVisible
 * @param {IndividualTaskReportItem[]} props.selectedTasks
 * @param {string} props.memberFullName
 * @param {Function} props.onEditCompleted
 */
const ModalBulkEditTasks = ({
  visible,
  setVisible,
  selectedTasks = [],
  memberFullName = "",
  onEditCompleted,
}) => {
  const {
    state,
    dispatch,
    hasOnlyOneProjectInitiallyAggregated,
    hasOnlyIntProjectAggregated,
    hasIntInInitiallySelectedTasks,
    isCategoryDisabled,
    selectedCategories,
    selectedModules,
    changeSelectedProject,
    isModuleDropdownDisabled,
    datesInitiallySelectedCount,
    changesCanBeSaved,
    initiallySelectedTasksHaveNoBilledTime,
  } = useModalBulkEditTaskFormState(selectedTasks, visible)
  const formFields = state.fields
  const noProjectSelected = formFields.project === undefined
  const noModuleSelected = formFields.module === undefined

  const rawInitiallySelectedProjects = selectedTasks
    .map((task) => {
      return {
        clientCode: task.clientCode,
        clientName: task.clientName,
        projectCode: task.projectCode,
        projectName: task.projectName,
      }
    })
    .reduce((acc, curr) => {
      if (!acc.find((v) => v.projectName === curr.projectName)) {
        acc.push(curr)
      }
      return acc
    }, [])

  let availableProjects = state.availableProjects.sort((a, b) => {
    const isActiveGroupHeader = a.label === "Activos" || b.label === "Inactivos"
    if (isActiveGroupHeader) {
      return 0
    }

    return (
      b.items[0].active - a.items[0].active ||
      a.items[0].isBillable - b.items[0].isBillable ||
      a.items[0].clientTagName.localeCompare(b.items[0].clientTagName)
    )
  })

  const activeProjects = availableProjects.filter((p) => p.items[0].active)
  const inactiveProjects = availableProjects.filter((p) => !p.items[0].active)

  if (activeProjects.length > 0) {
    activeProjects.unshift({
      label: "Activos",
      clientName: "",
      items: [
        {
          projectName: [...activeProjects]
            .flatMap((p) => p.items)
            .flatMap((i) => i.projectName)
            .join(" "),
        },
      ],
    })
  }

  if (inactiveProjects.length > 0) {
    inactiveProjects.unshift({
      label: "Inactivos",
      items: [
        {
          projectName: [...inactiveProjects]
            .flatMap((p) => p.items)
            .flatMap((i) => i.projectName)
            .join(" "),
        },
      ],
    })
  }
  availableProjects = [...activeProjects, ...inactiveProjects]

  const [savingChanges, setSavingChanges] = useState(false)

  return (
    <ModalForm
      visible={visible}
      setVisible={setVisible}
      loading={savingChanges}
      disabledSaveBtn={!changesCanBeSaved}
      service={async () => {
        setSavingChanges(true)
        const taskIds = selectedTasks.map((t) => t.taskId)
        let newProject

        if (formFields.project) {
          const project = state.availableProjects.find((p) =>
            p.items.find((i) => i.projectName === formFields.project)
          )
          newProject = project?.items.find(
            (i) => i.projectName === formFields.project
          ).projectID
        }

        try {
          await bulkEditTasks({
            taskIds,
            toChange: {
              newDate: formFields.date?.toFormat("yyyy-MM-dd"),
              newProject,
              newProjectUsesSpecialCategories:
                formFields.selectedProjectUsesSpecialCategories,
              newCategory: formFields.category,
              deleteModule: state.availableModules.length === 0,
              newModule: formFields.module,
              newComment: formFields.comment,
              newDeleteClientHours: formFields.shouldDeleteBilledTime,
              newDeleteClientHoursComment: formFields.timeDifferenceComment,
            },
          })
          dispatch(resetFields(formFields.selectedProjectUsesSpecialCategories))
          onEditCompleted()
        } catch (error) {
          onEditCompleted(error.message)
        } finally {
          setSavingChanges(false)
        }
      }}
      cleanUp={() =>
        dispatch(resetFields(formFields.selectedProjectUsesSpecialCategories))
      }
      header={
        <div className="flex gap-3 align-items-center">
          <i
            className="pi pi-pencil text-4xl"
            style={{ color: "var(--clr-info__dark)" }}
          />
          <div className="flex flex-column gap-2">
            <span className="text-3xl font-semibold font-cairo line-height-1">
              Edición múltiple de tareas
            </span>
            <span className="text-base font-normal flex align-items-center gap-2">
              {selectedTasks.length} tarea{selectedTasks.length > 1 ? "s" : ""}{" "}
              seleccionada{selectedTasks.length > 1 ? "s" : ""}
              <span>|</span> Integrante: {memberFullName}
            </span>
          </div>
        </div>
      }
      className="bulk-edit-tasks-modal"
    >
      <MessageRect
        severity="info"
        title="No es regaño, pero... ¿¡Qué estás haciendo!?"
      >
        Lo que edites reemplazará la información de todas las tareas
        seleccionadas. Respira hondo, revisa bien y evita una tragedia.
      </MessageRect>
      <div className="flex flex-column gap-3">
        <div className="flex gap-3">
          <div className="flex flex-column gap-2 flex-1 min-w-0">
            <div className="field-checkbox">
              <InputCheckbox
                disabled={!formFields.date}
                checked={!!formFields.date}
                inputId="changed-dates"
                icon={() => checkboxIcon(() => dispatch(resetDate()))}
                className={!formFields.date ? "opacity-40" : ""}
                onChange={(e) => {
                  dispatch(resetDate())
                }}
              />
              <label
                className={!formFields.date ? "opacity-40" : ""}
                htmlFor="changed-dates"
              >
                Fecha
              </label>
            </div>
            <div className="datepicker-container relative">
              <p className="absolute z-2">
                {formFields.date ?
                  `${String(formFields.date.day).padStart(2, "0")}/${String(formFields.date.month).padStart(2, "0")}/${formFields.date.year}`
                : `${datesInitiallySelectedCount} fecha${datesInitiallySelectedCount > 1 ? "s" : ""} seleccionada${datesInitiallySelectedCount > 1 ? "s" : ""}`
                }
              </p>
              <MyDatePicker
                inputReadOnly
                maxDate={DateTime.now()}
                allowClear={false}
                multiple
                popupClassName={clsx(
                  "bulk-edit-tasks-modal-datepicker",
                  !!formFields.date === true && "prevent-selected-deselect",
                  state.initiallySelectedDates.length === 1 &&
                    "prevent-selected-deselect"
                )}
                value={
                  formFields.date ?
                    [formFields.date]
                  : state.initiallySelectedDates
                }
                onCalendarChange={(dates) => {
                  if (!formFields.date) {
                    if (state.initiallySelectedDates.length > 1) {
                      const removedInitialDates =
                        state.initiallySelectedDates.filter((initialDate) => {
                          return !dates.some((d) =>
                            compareDates(d, initialDate)
                          )
                        })

                      if (removedInitialDates.length > 0) {
                        dispatch(updateDate(removedInitialDates[0]))
                        return
                      }
                    }

                    const selectedDate = dates.filter((d) => {
                      return !state.initiallySelectedDates.some((ud) =>
                        compareDates(d, ud)
                      )
                    })
                    dispatch(updateDate(selectedDate[0]))
                  } else {
                    // Check if there's only one initial date and user clicked on it
                    if (state.initiallySelectedDates.length === 1) {
                      const clickedOriginalDate = dates.some((d) =>
                        compareDates(d, state.initiallySelectedDates[0])
                      )

                      if (clickedOriginalDate) {
                        dispatch(resetDate())
                        return
                      }
                    }

                    // Check if user clicked on an initially selected date
                    const clickedInitialDate =
                      state.initiallySelectedDates.find((initialDate) => {
                        return (
                          dates.some((d) => compareDates(d, initialDate)) &&
                          !compareDates(initialDate, formFields.date)
                        )
                      })

                    if (clickedInitialDate) {
                      dispatch(updateDate(clickedInitialDate))
                      return
                    }

                    const newDates = dates.filter((d) => {
                      return !state.initiallySelectedDates.some((td) =>
                        compareDates(d, td)
                      )
                    })
                    const newDate = newDates.filter((d) => {
                      return !compareDates(d, formFields.date)
                    })[0]

                    if (newDate) {
                      dispatch(updateDate(newDate))
                    } else {
                      dispatch(updateDate(newDates[1]))
                    }
                  }
                }}
              />
            </div>
          </div>
          <div className="flex flex-column gap-2 flex-1 min-w-0">
            <div className="field-checkbox">
              <InputCheckbox
                icon={() => checkboxIcon(() => dispatch(resetProject()))}
                inputId="changed-project"
                disabled={noProjectSelected}
                className={noProjectSelected ? "opacity-40" : ""}
                checked={!!formFields.project}
                onChange={() => {
                  dispatch(resetProject())
                }}
              />
              <label
                className={noProjectSelected ? "opacity-40" : ""}
                htmlFor="changed-project"
              >
                Proyecto
              </label>
            </div>
            <Dropdown
              options={availableProjects}
              filter
              filterBy="projectName"
              resetFilterOnHide
              value={formFields.project}
              onChange={(e) => {
                changeSelectedProject(e.value)
              }}
              scrollHeight="480px"
              optionLabel="label"
              optionValue="projectName"
              placeholder={(() => {
                if (noProjectSelected) {
                  return hasOnlyOneProjectInitiallyAggregated ?
                      <div className="w-min">
                        <ClientProjectChip
                          clientCode={
                            rawInitiallySelectedProjects[0].clientCode
                          }
                          projectCode={
                            rawInitiallySelectedProjects[0].projectCode
                          }
                        />
                      </div>
                    : `${rawInitiallySelectedProjects.length} proyecto${rawInitiallySelectedProjects.length > 1 ? "s" : ""} seleccionado${rawInitiallySelectedProjects.length > 1 ? "s" : ""}`
                } else {
                  const client = state.availableProjects.find((p) =>
                    p.items.find((i) => i.projectName === formFields.project)
                  )
                  const project = client?.items.find(
                    (i) => i.projectName === formFields.project
                  )
                  if (project) {
                    return (
                      <div className="w-min">
                        <ClientProjectChip
                          clientCode={client?.label}
                          projectCode={project?.projectTagName}
                        />
                      </div>
                    )
                  } else {
                    return "CLIENT NOT FOUND"
                  }
                }
              })()}
              optionGroupLabel="label"
              optionGroupChildren="items"
              optionGroupTemplate={(el) => {
                const isActiveHeader =
                  el.label === "Activos" || el.label === "Inactivos"

                if (isActiveHeader) {
                  return (
                    <p
                      className=" sticky top-0 separator-for-active-inactive-group"
                      style={{
                        background:
                          "light-dark(var(--surface-highlight__light), var(--surface-canva__light))",
                        paddingTop: "8px",
                        paddingBottom: "8px",
                        zIndex: "10",
                        color: "light-dark(rgb(52, 58, 64), white)",
                      }}
                    >
                      {el.label}
                    </p>
                  )
                }

                return (
                  <div
                    data-active={el.items[0].active ? "true" : "false"}
                    className="flex gap-2 py-2 align-items-center"
                  >
                    <ClientChip clientCode={el.label} />{" "}
                    <span>{el.clientName}</span>
                  </div>
                )
              }}
              panelClassName={clsx(
                "max-w-23rem",
                "bulk-edit-tasks-modal-project-dropdown-panel",
                noProjectSelected && "no-project-selected"
              )}
              itemTemplate={(el) => {
                return (
                  <div
                    className={clsx(
                      "flex gap-2 ml-5 align-items-center overflow-hidden text-overflow-ellipsis whitespace-nowrap",
                      rawInitiallySelectedProjects.some(
                        (p) => p.projectName === el.projectName
                      ) && "originally-selected-project"
                    )}
                  >
                    <ProjectChip
                      clientCode={el.clientTagName}
                      projectCode={el.projectTagName}
                    />
                    <span className="max-w-23rem overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                      {el.projectName}
                    </span>
                  </div>
                )
              }}
            />
          </div>
        </div>
        <div className="flex gap-3">
          <div className="flex flex-column gap-2 flex-1 min-w-0">
            <div className="field-checkbox">
              <InputCheckbox
                icon={() => checkboxIcon(() => dispatch(resetCategory()))}
                disabled={isCategoryDisabled || !formFields.category}
                className={
                  isCategoryDisabled || !formFields.category ? "opacity-40" : ""
                }
                inputId="category"
                checked={!!formFields.category}
                onChange={() => {
                  dispatch(resetCategory())
                }}
              />
              <label
                className={
                  isCategoryDisabled || !formFields.category ? "opacity-40" : ""
                }
                htmlFor="category"
              >
                Categoría
              </label>
            </div>
            <DropdownCategoryFilter
              className="bulk-edit-tasks-modal-category-dropdown"
              options={state.availableCategories}
              placeholder={(() => {
                if (
                  formFields.project &&
                  state.availableCategories.length !== 0 &&
                  !formFields.category
                )
                  return "Sin categoría"
                if (state.isFetchingCategories) return "Cargando..."
                return `${selectedCategories.length} categoría${selectedCategories.length > 1 ? "s" : ""} seleccionad${selectedCategories.length > 1 ? "as" : "a"}`
              })()}
              scrollHeight="350px"
              disabled={isCategoryDisabled}
              value={formFields.category}
              onChange={(e) => {
                dispatch(updateCategory(e.value))
              }}
            />
            {formFields.project &&
              state.availableCategories.length !== 0 &&
              !formFields.category && (
                <ErrorMessageForm errorMessage="Este campo no puede estar vacío" />
              )}
          </div>
          <div className="flex flex-column gap-2 flex-1 min-w-0">
            <div className="field-checkbox">
              <InputCheckbox
                icon={() => checkboxIcon(() => dispatch(resetModule()))}
                disabled={!formFields.module}
                inputId="module"
                className={!formFields.module ? "opacity-40" : ""}
                checked={!!formFields.module}
                onChange={() => {
                  dispatch(resetModule())
                }}
              />
              <label
                className={!formFields.module ? "opacity-40" : ""}
                htmlFor="module"
              >
                Módulo
              </label>
            </div>
            <DropdownInput
              resetFilterOnHide={true}
              filter
              className="modules-dropdown"
              placeholder={(() => {
                if (state.isFetchingModules) return "Cargando..."
                if (selectedModules.length === 0) return "Sin módulos"
                if (formFields.project && state.availableModules.length === 0)
                  return "Sin módulos"

                if (
                  formFields.project &&
                  state.availableModules.length !== 0 &&
                  !formFields.module
                )
                  return "Sin módulo"
                return `${selectedModules.length} módulo${selectedModules.length > 1 ? "s" : ""} seleccionado${selectedModules.length > 1 ? "s" : ""}`
              })()}
              panelClassName={clsx(
                "max-w-23rem",
                noModuleSelected && "bulk-edit-tasks-modal-module-dropdown"
              )}
              disabled={isModuleDropdownDisabled}
              options={state.availableModules}
              optionValue="moduleID"
              optionLabel="moduleName"
              value={formFields.module}
              onChange={(e) => {
                dispatch(updateModule(e.value))
              }}
              itemTemplate={(option) => {
                return (
                  <span
                    className={clsx(
                      "overflow-hidden text-overflow-ellipsis whitespace-nowrap",
                      selectedModules.some((m) => m === option.moduleName) &&
                        "originally-selected-module"
                    )}
                  >
                    {option.moduleName}
                  </span>
                )
              }}
              scrollHeight="350px"
              emptyMessage="No hay modulos aquí... ¡este cliente es minimalista!"
            />
            {formFields.project &&
              state.availableModules.length !== 0 &&
              !formFields.module && (
                <ErrorMessageForm errorMessage="Este campo no puede estar vacío" />
              )}
          </div>
        </div>
        <div className="flex flex-column gap-2 w-full">
          <div className="field-checkbox">
            <InputCheckbox
              icon={() => checkboxIcon(() => dispatch(resetComment()))}
              name="comment"
              inputId="comment"
              checked={formFields.comment.trim() !== ""}
              onChange={() => dispatch(resetComment())}
              disabled={formFields.comment.trim() === ""}
              className={formFields.comment.trim() === "" ? "opacity-40" : ""}
            />
            <label
              style={{ opacity: formFields.comment.trim() === "" ? 0.4 : 1 }}
              htmlFor="comment"
            >
              Comentario (opcional)
            </label>
          </div>
          <InputText
            placeholder="Agrega un comentario a la selección"
            value={formFields.comment}
            onChange={(e) => {
              if (e.target.value === "") {
                dispatch(resetComment())
              } else {
                dispatch(updateComment(e.target.value))
              }
            }}
            onBlur={(e) => {
              dispatch(updateComment(e.target.value.trim()))
            }}
          />
        </div>
        {!initiallySelectedTasksHaveNoBilledTime && (
          <div className="flex flex-column gap-3 w-full">
            <div className="flex flex-column gap-2">
              <div className="field-checkbox">
                <InputCheckbox
                  icon={() =>
                    checkboxIcon(() => dispatch(resetDeleteClientBilledTime()))
                  }
                  inputId="delete-client-hours"
                  name="date"
                  checked={formFields.shouldDeleteBilledTime}
                  disabled={
                    !formFields.shouldDeleteBilledTime ||
                    hasOnlyIntProjectAggregated
                  }
                  className={
                    (
                      !formFields.shouldDeleteBilledTime ||
                      hasOnlyIntProjectAggregated
                    ) ?
                      "opacity-40"
                    : ""
                  }
                  onChange={(e) => {
                    dispatch(resetDeleteClientBilledTime(e.value))
                  }}
                />
                <label
                  className={
                    (
                      !formFields.shouldDeleteBilledTime ||
                      hasOnlyIntProjectAggregated
                    ) ?
                      "opacity-40"
                    : ""
                  }
                  htmlFor="delete-client-hours"
                >
                  Duración
                </label>
              </div>
              <InputSwitchForm
                className="mt-1 success-slider"
                label="Borrar horas cliente"
                labelClassName={
                  (
                    hasOnlyIntProjectAggregated ||
                    (!formFields.project && hasIntInInitiallySelectedTasks)
                  ) ?
                    "opacity-40"
                  : ""
                }
                disabled={
                  hasOnlyIntProjectAggregated ||
                  (!formFields.project && hasIntInInitiallySelectedTasks)
                }
                value={formFields.shouldDeleteBilledTime}
                onChange={(e) => {
                  dispatch(updateDeleteClientBilledTime(e.value))
                }}
              />
            </div>
            {formFields.shouldDeleteBilledTime && (
              <div className="flex flex-column w-full">
                <label
                  htmlFor="time-difference-comment"
                  style={{
                    color: "var(--text-color)",
                    marginBottom: "3.5px",
                    opacity: !formFields.shouldDeleteBilledTime ? 0.7 : 1,
                  }}
                >
                  Motivo de la diferencia
                </label>
                <InputText
                  id="time-difference-comment"
                  label="Agrega un motivo a la selección"
                  disabled={!formFields.shouldDeleteBilledTime}
                  placeholder="Agrega un motivo a la selección"
                  value={formFields.timeDifferenceComment}
                  onChange={(e) => {
                    if (e.target.value === "") {
                      dispatch(resetTimeDifferenceComment())
                    } else {
                      dispatch(updateTimeDifferenceComment(e.target.value))
                    }
                  }}
                  onBlur={(e) => {
                    dispatch(updateTimeDifferenceComment(e.target.value.trim()))
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </ModalForm>
  )
}

export default ModalBulkEditTasks
